import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Button,
  TextField,
  MenuItem,
  Box,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Snackbar,
  Alert,
  Tooltip,
  CircularProgress,
  Checkbox
} from '@mui/material';
import {
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  Assessment as ReportsIcon,
  CallMade as CallIcon,
  Assignment as DispoIcon,
  Refresh as RefreshIcon,
  Download as ExportIcon,
  Close as CloseIcon,
  PlayArrow as PlayArrowIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Analytics as AnalyticsIcon,
  Settings as SettingsIcon,
  CheckCircle as CheckCircleIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import FieldMappingManager from '../components/FieldMappingManager';
import DispositionSettings from '../components/DispositionSettings';

import { getCallRecording } from '../services/aiService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DISPOSITION_OPTIONS = [
  { value: 'NEW', label: 'New', color: 'info' },
  { value: 'APPROVED_FOR_QA', label: 'Approved for QA', color: 'primary' },
  { value: 'REJECTED_BAD_DATA', label: 'Rejected - Bad Data', color: 'error' },
  { value: 'REJECTED_DUPLICATE', label: 'Rejected - Duplicate', color: 'error' },
  { value: 'REJECTED_AGENT_ERROR', label: 'Rejected - Agent Error', color: 'error' },
  { value: 'NEEDS_FOLLOW_UP', label: 'Needs Follow-Up', color: 'warning' },
  { value: 'PENDING_REVIEW', label: 'Pending Review', color: 'secondary' },
  { value: 'SEND_TO_SALES', label: 'Send to Sales', color: 'success' },
  { value: 'QA_COMPLETED', label: 'QA Completed', color: 'success' },
  { value: 'SALE', label: 'SALE', color: 'success' },
  { value: 'DNC', label: 'DNC', color: 'error' }
];

const LEAD_STATUS_OPTIONS = [
  { value: 'NEW', label: 'New', color: 'success' },        // Green
  { value: 'PENDING', label: 'Pending', color: 'warning' }, // Orange/Yellow
  { value: 'CONTACTED', label: 'Contacted', color: 'default' }, // Gray
  { value: 'QUALIFIED', label: 'Qualified', color: 'success' },    // Green (same as NEW for positive status)
  { value: 'NOT_QUALIFIED', label: 'Not Qualified', color: 'error' }, // Red
  { value: 'CALLBACK', label: 'Callback', color: 'info' }, // Light Blue
  { value: 'SENT_TO_QA', label: 'Sent to QA', color: 'primary' }, // Blue
  { value: 'DO_NOT_CALL', label: 'Do Not Call', color: 'error' } // Red (same as NOT_QUALIFIED since both are negative)
];

const Leads = () => {
  const { token, user } = useAuth();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState({
    status: '',
    disposition: '',
    campaign: '',
    list_id: '',
    search: '',
    startDate: '',
    endDate: ''
  });
  const [selectedLead, setSelectedLead] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedLead, setEditedLead] = useState(null);
  const [dispoDialogOpen, setDispoDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [fieldMappingDialogOpen, setFieldMappingDialogOpen] = useState(false);
  const [newDisposition, setNewDisposition] = useState('');
  const [dispoNotes, setDispoNotes] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [stats, setStats] = useState({
    total: 0,
    new: 0,
    contacted: 0,
    qualified: 0,
    closed: 0
  });
  const [fieldMappings, setFieldMappings] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [listIds, setListIds] = useState([]);
  const [dispositionSettingsOpen, setDispositionSettingsOpen] = useState(false);
  const [autoTriggerDispositions, setAutoTriggerDispositions] = useState(['SALE', 'QUALIFIED', 'FOLLOW_UP']);
  const [analyticsData, setAnalyticsData] = useState({
    listDispo: [],
    campaignDispo: [],
    listPerformance: [],
    campaignPerformance: [],
    totalStats: {
      total_leads: 0,
      new_leads: 0,
      sent_to_qa: 0,
      pending: 0,
      callback: 0,
      dnc: 0
    }
  });
  const [listIdViewMode, setListIdViewMode] = useState('top10'); // 'top10', 'all', 'search'
  const [listIdSearchTerm, setListIdSearchTerm] = useState('');
  const [showAllListIds, setShowAllListIds] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [bulkDispoDialogOpen, setBulkDispoDialogOpen] = useState(false);
  const [bulkDisposition, setBulkDisposition] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const [dashboardDateFilter, setDashboardDateFilter] = useState('today');
  const [dashboardStartDate, setDashboardStartDate] = useState(new Date().toISOString().split('T')[0]);
  const [dashboardEndDate, setDashboardEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [availableDispositions, setAvailableDispositions] = useState([]);
  
  // Audio playback state
  const [currentPlayingCall, setCurrentPlayingCall] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioError, setAudioError] = useState(null);

  useEffect(() => {
    fetchLeads();
    fetchStats();
    fetchAnalytics();
  }, [page, rowsPerPage, filters]);

  // Refetch stats when dashboard date filters change
  useEffect(() => {
    fetchStats();
    fetchAnalytics();
  }, [dashboardDateFilter, dashboardStartDate, dashboardEndDate]);



  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page + 1,
        limit: rowsPerPage,
        ...Object.fromEntries(Object.entries(filters).filter(([_, value]) => value))
      });

      const response = await axios.get(`${API_BASE_URL}/leads?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setLeads(response.data.data || []);
        setTotalCount(response.data.count || 0);
        
        // Clear selected leads when data changes
        setSelectedLeads([]);
        
        // Store field mappings from response
        if (response.data.fieldMappings) {
          setFieldMappings(response.data.fieldMappings);
        }

        // Extract unique campaigns and list IDs for filters
        const uniqueCampaigns = [...new Set(response.data.data.map(lead => lead.campaign).filter(Boolean))];
        const uniqueListIds = [...new Set(response.data.data.map(lead => lead.list_id).filter(Boolean))];
        const uniqueDispositions = [...new Set(response.data.data.map(lead => lead.disposition).filter(Boolean))];
        setCampaigns(uniqueCampaigns);
        setListIds(uniqueListIds);
        setAvailableDispositions(uniqueDispositions);
      }
    } catch (error) {
      console.error('Error fetching leads:', error);
      showSnackbar('Error fetching leads', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const params = new URLSearchParams();
      
      // Add date filtering based on dashboard filter
      if (dashboardDateFilter === 'today') {
        const today = new Date().toISOString().split('T')[0];
        params.append('startDate', today);
        params.append('endDate', today);
      } else if (dashboardDateFilter === 'custom') {
        if (dashboardStartDate) params.append('startDate', dashboardStartDate);
        if (dashboardEndDate) params.append('endDate', dashboardEndDate);
      }
      // If 'all', don't add date parameters
      
      const response = await axios.get(`${API_BASE_URL}/leads/stats?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setStats(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const params = new URLSearchParams();
      
      // Add date filtering based on dashboard filter (same as fetchStats)
      if (dashboardDateFilter === 'today') {
        const today = new Date().toISOString().split('T')[0];
        params.append('startDate', today);
        params.append('endDate', today);
      } else if (dashboardDateFilter === 'custom') {
        if (dashboardStartDate) params.append('startDate', dashboardStartDate);
        if (dashboardEndDate) params.append('endDate', dashboardEndDate);
      }
      // If 'all', don't add date parameters
      
      const response = await axios.get(`${API_BASE_URL}/leads/analytics?${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        setAnalyticsData({
          listDispo: data.listDispo || [],
          campaignDispo: data.campaignDispo || [],
          listPerformance: data.listPerformance || [],
          campaignPerformance: data.campaignPerformance || [],
          totalStats: data.totalStats || {
            total_leads: 0,
            new_leads: 0,
            xfer: 0,
            busxfr: 0,
            ni: 0,
            sent_to_qa: 0,
            dnc: 0
          }
        });

        // Update stats with filtered totals
        // Note: new_leads from backend is actually SALE count (disposition = 'SALE')
        if (data.totalStats) {
          setStats({
            total: data.totalStats.total_leads || 0,
            new: data.totalStats.new_leads || 0, // This is SALE count
            sent_to_qa: data.totalStats.sent_to_qa || 0,
            xfer: data.totalStats.xfer || 0,
            busxfr: data.totalStats.busxfr || 0,
            ni: data.totalStats.ni || 0,
            dnc: data.totalStats.dnc || 0
          });
        }
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
      // Reset to default values on error
      setAnalyticsData({
        listDispo: [],
        campaignDispo: [],
        listPerformance: [],
        campaignPerformance: [],
        totalStats: {
          total_leads: 0,
          new_leads: 0,
          xfer: 0,
          ni: 0,
          sent_to_qa: 0,
          dnc: 0
        }
      });
    }
  };

  const handleDispositionUpdate = async () => {
    if (!selectedLead || !newDisposition) return;

    try {
      const response = await axios.put(
        `${API_BASE_URL}/leads/${selectedLead.id}/disposition`,
        {
          disposition: newDisposition,
          notes: dispoNotes,
          moveToAnalysis: autoTriggerDispositions.includes(newDisposition)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showSnackbar('Disposition updated successfully', 'success');
        setDispoDialogOpen(false);
        setNewDisposition('');
        setDispoNotes('');
        setSelectedLead(null);
        fetchLeads();
        fetchStats();
      }
    } catch (error) {
      console.error('Error updating disposition:', error);
      showSnackbar('Error updating disposition', 'error');
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = LEAD_STATUS_OPTIONS.find(s => s.value === status) || 
                        { label: status, color: 'default' };
    
    return (
      <Chip 
        label={statusConfig.label} 
        color={statusConfig.color} 
        size="small" 
        variant="outlined"
      />
    );
  };

  const getDispositionChip = (disposition) => {
    if (!disposition) return <Chip label="No Disposition" size="small" variant="outlined" />;
    
    const dispoConfig = DISPOSITION_OPTIONS.find(d => d.value === disposition) || 
                       { label: disposition, color: 'default' };
    return (
      <Chip 
        label={dispoConfig.label} 
        color={dispoConfig.color} 
        size="small"
      />
    );
  };

  const openDispoDialog = (lead) => {
    setSelectedLead(lead);
    setNewDisposition(lead.Call?.disposition || '');
    setDispoNotes(lead.notes?.disposition || '');
    setDispoDialogOpen(true);
  };

  const openDetailDialog = (lead) => {
    setSelectedLead(lead);
    setEditedLead(lead);
    setEditMode(false);
    setDetailDialogOpen(true);
  };

  const handleEditMode = () => {
    setEditMode(true);
    setEditedLead({ ...selectedLead });
  };

  const handleCancelEdit = () => {
    setEditMode(false);
    setEditedLead(selectedLead);
  };

  const handleFieldChange = (field, value) => {
    setEditedLead(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEdit = async () => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/leads/${selectedLead.id}`,
        editedLead,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        showSnackbar('Lead updated successfully', 'success');
        setEditMode(false);
        setSelectedLead(response.data.data);
        setEditedLead(response.data.data);
        fetchLeads(); // Refresh the leads list
      }
    } catch (error) {
      console.error('Error updating lead:', error);
      showSnackbar(
        error.response?.data?.error || 'Error updating lead', 
        'error'
      );
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filtering
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      disposition: '',
      campaign: '',
      list_id: '',
      search: '',
      startDate: '',
      endDate: ''
    });
  };

  const exportLeads = async () => {
    try {
      const params = new URLSearchParams({
        export: 'true',
        ...Object.fromEntries(Object.entries(filters).filter(([_, value]) => value))
      });

      const response = await axios.get(`${API_BASE_URL}/leads/export?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `leads_export_${new Date().toISOString().split('T')[0]}.csv`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      showSnackbar('Leads exported successfully', 'success');
    } catch (error) {
      console.error('Error exporting leads:', error);
      showSnackbar('Error exporting leads', 'error');
    }
  };

  const handleLeadClick = (lead) => {
    setSelectedLead(lead);
    setDetailDialogOpen(true);
  };

  const formatLeadData = (lead) => {
    return {
      'VICIdial Lead ID': lead.vici_lead_id || 'N/A',
      'List ID': lead.list_id || 'N/A', // For monitoring lead suppliers
      'Vendor Code': lead.vendor_lead_code || 'N/A',
      'Campaign': lead.campaign || 'N/A',
      'Full Name': lead.fullname || `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'N/A',
      'Title': lead.title || 'N/A',
      'Email': lead.email || 'N/A',
      'Phone Number': lead.phone_number || 'N/A',
      'Alt Phone': lead.alt_phone || 'N/A',
      'Dialed Number': lead.dialed_number || 'N/A',
      'Address': [
        lead.address1,
        lead.address2,
        lead.address3
      ].filter(Boolean).join(', ') || 'N/A',
      'City': lead.city || 'N/A',
      'Postal Code': lead.postal_code || 'N/A',
      'County': lead.county || 'N/A',
      'Date of Birth': lead.date_of_birth ? new Date(lead.date_of_birth).toLocaleDateString() : 'N/A',
      'Security Phrase': lead.security_phrase || 'N/A',
      'Called Count': lead.called_count || 0,
      'Comments': lead.comments || 'N/A',
      'Status': lead.status,
      'Disposition': lead.disposition || 'N/A',
      'Created': new Date(lead.created_at).toLocaleDateString(),
      'Updated': new Date(lead.updated_at).toLocaleDateString()
    };
  };

  const getStatusColor = (status) => {
    const statusConfig = LEAD_STATUS_OPTIONS.find(s => s.value === status) || 
                        { color: 'default' };
    return statusConfig.color;
  };

  const getDispositionColor = (disposition) => {
    const dispoConfig = DISPOSITION_OPTIONS.find(d => d.value === disposition) || 
                       { color: 'default' };
    return dispoConfig.color;
  };

  const handleMoveToAnalysis = async (lead) => {
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_BASE_URL}/leads/${lead.id}/move-to-analysis`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        showSnackbar('Lead moved to Call Analysis successfully', 'success');
        fetchLeads(); // Refresh the leads list
      } else {
        showSnackbar('Failed to move lead to Call Analysis', 'error');
      }
    } catch (error) {
      console.error('Error moving lead to analysis:', error);
      showSnackbar('Error moving lead to Call Analysis', 'error');
    } finally {
      setLoading(false);
    }
  };



  const handleUpdateDisposition = (lead) => {
    // Implementation of handleUpdateDisposition function
  };

  const handleSaveDispositionSettings = (newSettings) => {
    setAutoTriggerDispositions(newSettings);
    // Save to localStorage for persistence
    localStorage.setItem('autoTriggerDispositions', JSON.stringify(newSettings));
    showSnackbar('Disposition settings saved successfully', 'success');
  };

  // Load settings from localStorage on component mount
  useEffect(() => {
    const savedSettings = localStorage.getItem('autoTriggerDispositions');
    if (savedSettings) {
      setAutoTriggerDispositions(JSON.parse(savedSettings));
    }
  }, []);

  // Helper function to format dates in DD/MM/YYYY format
  const formatDate = (dateString, includeTime = false) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    
    if (includeTime) {
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    }
    
    return `${day}/${month}/${year}`;
  };

  // Helper function to get custom field label
  const getFieldLabel = (originalField, defaultLabel = null) => {
    if (fieldMappings[originalField]) {
      return fieldMappings[originalField].label;
    }
    return defaultLabel || originalField.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Helper function to format lead data with custom labels
  const formatLeadDataWithMappings = (lead) => {
    const formattedData = {};
    
    // Apply field mappings to create display data
    Object.keys(lead).forEach(field => {
      if (fieldMappings[field] && fieldMappings[field].visible) {
        const label = fieldMappings[field].label;
        const value = lead[field];
        
        // Format specific field types
        if (field === 'date_of_birth' && value) {
          formattedData[label] = new Date(value).toLocaleDateString();
        } else if (field === 'created_at' && value) {
          formattedData[label] = new Date(value).toLocaleDateString();
        } else if (field === 'updated_at' && value) {
          formattedData[label] = new Date(value).toLocaleDateString();
        } else {
          formattedData[label] = value || 'N/A';
        }
      }
    });

    return formattedData;
  };

  // Helper function to extract agent username from VICIdial format
  const getAgentName = (lead) => {
    // Try to get agent name from different possible fields
    if (lead.agent_name) return lead.agent_name;
    if (lead.agent_full_name) return lead.agent_full_name;
    if (lead.agent_username) return lead.agent_username;
    if (lead.Call && lead.Call.agent_username) return lead.Call.agent_username;
    
    // Check if agent data is stored in the data field
    if (lead.data) {
      if (lead.data.agent_username) return lead.data.agent_username;
      if (lead.data.agent_name) return lead.data.agent_name;
      if (lead.data.agent_id) return lead.data.agent_id;
      
      // Check for VICIdial format with --A-- and --B--
      if (lead.data.user && typeof lead.data.user === 'string' && 
          lead.data.user.includes('--A--') && lead.data.user.includes('--B--')) {
        const match = lead.data.user.match(/--A--(.+?)--B--/);
        if (match && match[1]) {
          return match[1]; // Return the username
        }
      }
      
      // If fullname in data is not the customer name (might be agent ID)
      if (lead.data.fullname && 
          lead.data.fullname !== lead.fullname && // Not the same as customer name
          !lead.data.fullname.includes('--A--')) {
        return lead.data.fullname;
      }
      
      // Fallback to user field
      if (lead.data.user) return lead.data.user;
    }
    
    return 'Agent N/A';
  };

  // Bulk selection handlers
  const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelectedLeads(leads.map(lead => lead.id));
    } else {
      setSelectedLeads([]);
    }
  };

  const handleSelectLead = (leadId) => {
    setSelectedLeads(prev => {
      if (prev.includes(leadId)) {
        return prev.filter(id => id !== leadId);
      } else {
        return [...prev, leadId];
      }
    });
  };

  const openBulkDispoDialog = () => {
    if (selectedLeads.length === 0) {
      showSnackbar('Please select leads to update', 'warning');
      return;
    }
    setBulkDispoDialogOpen(true);
  };

  const handleBulkDispositionUpdate = async () => {
    if (!bulkDisposition || selectedLeads.length === 0) return;

    try {
      const response = await axios.put(
        `${API_BASE_URL}/leads/bulk-disposition`,
        {
          leadIds: selectedLeads,
          disposition: bulkDisposition,
          notes: bulkNotes
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      if (response.data.success) {
        showSnackbar(`Updated disposition for ${selectedLeads.length} leads`, 'success');
        setBulkDispoDialogOpen(false);
        setBulkDisposition('');
        setBulkNotes('');
        setSelectedLeads([]);
        fetchLeads();
        fetchStats();
      }
    } catch (error) {
      console.error('Error updating bulk disposition:', error);
      showSnackbar('Error updating dispositions', 'error');
    }
  };

  // Audio playback functions
  const handlePlayRecording = async (lead) => {
    try {
      // If this is already playing, pause it
      if (currentPlayingCall === lead.call_id && isPlaying) {
        setIsPlaying(false);
        return;
      }

      // Stop any currently playing audio
      if (currentPlayingCall && currentPlayingCall !== lead.call_id) {
        handleAudioStop();
      }

      // Start playing this recording
      if (lead.call_id) {
        setCurrentPlayingCall(lead.call_id);
        setAudioError(null);
        
        // Get the recording URL if not already loaded
        if (!audioUrl || currentPlayingCall !== lead.call_id) {
          const recordingData = await getCallRecording(lead.call_id);
          const audioBlob = new Blob([recordingData], { type: 'audio/wav' });
          const url = URL.createObjectURL(audioBlob);
          setAudioUrl(url);
        }
        
        setIsPlaying(true);
      } else {
        showSnackbar('No call recording available for this lead', 'warning');
      }
    } catch (error) {
      console.error('Error playing recording:', error);
      setAudioError(error.message);
      showSnackbar('Error loading recording', 'error');
    }
  };

  const handleAudioPlay = () => {
    setIsPlaying(true);
  };

  const handleAudioPause = () => {
    setIsPlaying(false);
  };

  const handleAudioStop = () => {
    setIsPlaying(false);
    setCurrentPlayingCall(null);
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
      setAudioUrl(null);
    }
  };

  const handleAudioError = (error) => {
    console.error('Audio playback error:', error);
    setAudioError('Error playing audio');
    setIsPlaying(false);
    showSnackbar('Error playing recording', 'error');
  };

  // Build list/campaign dispo maps and derive XFER/NI totals from same data as breakdown (so cards match)
  const listDispoMap = {};
  let xferFromListDispo = 0;
  let niFromListDispo = 0;
  (analyticsData.listDispo || []).forEach(row => {
    if (!listDispoMap[row.list_id]) listDispoMap[row.list_id] = {};
    listDispoMap[row.list_id][row.disposition] = row.count;
    const d = (row.disposition || '').toUpperCase().trim();
    if (d === 'XFER') xferFromListDispo += Number(row.count) || 0;
    if (d === 'NI') niFromListDispo += Number(row.count) || 0;
  });
  const allDispos = Array.from(new Set((analyticsData.listDispo || []).map(row => row.disposition)));

  // Performance by List ID
  const campaignDispoMap = {};
  (analyticsData.campaignDispo || []).forEach(row => {
    if (!campaignDispoMap[row.campaign]) campaignDispoMap[row.campaign] = {};
    campaignDispoMap[row.campaign][row.disposition] = row.count;
  });
  const allCampaignDispos = Array.from(new Set((analyticsData.campaignDispo || []).map(row => row.disposition)));

  console.log('listDispo', analyticsData.listDispo);
  console.log('campaignDispo', analyticsData.campaignDispo);

  return (
    <Container maxWidth="xl">
      <Box mb={3}>
        <Typography variant="h4" gutterBottom>
          Lead Management
        </Typography>
        <Typography variant="subtitle1" color="textSecondary">
          Manage leads, dispositions, and track performance
        </Typography>
      </Box>

      {/* Dashboard Date Filter Controls */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Dashboard Filter</InputLabel>
              <Select
                value={dashboardDateFilter}
                label="Dashboard Filter"
                onChange={(e) => setDashboardDateFilter(e.target.value)}
              >
                <MenuItem value="today">Today Only</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="custom">Custom Date Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          {dashboardDateFilter === 'custom' && (
            <>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Start Date"
                  type="date"
                  value={dashboardStartDate}
                  onChange={(e) => setDashboardStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="End Date"
                  type="date"
                  value={dashboardEndDate}
                  onChange={(e) => setDashboardEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
            </>
          )}
          
          <Grid item xs={12} sm={6} md={3}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" color="text.secondary">
                {dashboardDateFilter === 'today' && `Showing: ${new Date().toLocaleDateString()}`}
                {dashboardDateFilter === 'all' && 'Showing: All Time'}
                {dashboardDateFilter === 'custom' && dashboardStartDate && dashboardEndDate && 
                  `Showing: ${new Date(dashboardStartDate).toLocaleDateString()} - ${new Date(dashboardEndDate).toLocaleDateString()}`}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Stats Cards: SALE, XFER, BUSXFR, NI, Sent to QA, DNC, Total Leads */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>SALE</Typography>
              <Typography variant="h4" color="success.main">{stats.new || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>XFER</Typography>
              <Typography variant="h4" color="info.main">{stats.xfer ?? 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>BUSXFR</Typography>
              <Typography variant="h4" color="secondary.main">{stats.busxfr ?? 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>NI</Typography>
              <Typography variant="h4" color="warning.main">{stats.ni ?? 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Sent to QA</Typography>
              <Typography variant="h4" color="primary.main">{stats.sent_to_qa || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>DNC</Typography>
              <Typography variant="h4" color="error.main">{stats.dnc || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Total Leads</Typography>
              <Typography variant="h4">{stats.total || 0}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Analytics Charts */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Performance by List ID
                </Typography>
                <Box display="flex" gap={1} alignItems="center">
                  <TextField
                    size="small"
                    placeholder="Search List ID..."
                    value={listIdSearchTerm}
                    onChange={(e) => setListIdSearchTerm(e.target.value)}
                    sx={{ width: 150 }}
                  />
                  <Button
                    size="small"
                    variant={showAllListIds ? "contained" : "outlined"}
                    onClick={() => setShowAllListIds(!showAllListIds)}
                  >
                    {showAllListIds ? 'Top 10' : 'Show All'}
                  </Button>
                </Box>
              </Box>
              
              <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
                {(() => {
                  console.log('analyticsData', analyticsData);
                  if (!analyticsData.listDispo || analyticsData.listDispo.length === 0) {
                    return <Typography color="text.secondary">No data available</Typography>;
                  }

                  // Filter the data based on search term
                  let filteredListIds = Object.keys(listDispoMap);
                  if (listIdSearchTerm) {
                    filteredListIds = filteredListIds.filter(listId => 
                      listId.toLowerCase().includes(listIdSearchTerm.toLowerCase())
                    );
                  }

                  // Sort by total count (sum of all dispositions)
                  filteredListIds.sort((a, b) => {
                    const totalA = Object.values(listDispoMap[a]).reduce((sum, count) => sum + count, 0);
                    const totalB = Object.values(listDispoMap[b]).reduce((sum, count) => sum + count, 0);
                    return totalB - totalA;
                  });

                  // Limit to top 10 if not showing all
                  if (!showAllListIds && !listIdSearchTerm) {
                    filteredListIds = filteredListIds.slice(0, 10);
                  }

                  return (
                    <>
                      {!showAllListIds && !listIdSearchTerm && Object.keys(listDispoMap).length > 10 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Showing top 10 performing List IDs. Use "Show All" to see complete list.
                        </Alert>
                      )}
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>List ID</TableCell>
                              <TableCell>Dispositions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredListIds.map(listId => (
                              <TableRow key={listId}>
                                <TableCell>
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {listId}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                                    {allDispos.map(dispo => {
                                      const count = listDispoMap[listId][dispo] || 0;
                                      if (count === 0) return null;
                                      return (
                                        <Chip
                                          key={dispo}
                                          label={`${dispo}: ${count}`}
                                          size="small"
                                          color={getDispositionColor(dispo)}
                                          variant="outlined"
                                        />
                                      );
                                    })}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  );
                })()}
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Performance by Campaign
              </Typography>
              <Box sx={{ maxHeight: 350, overflow: 'auto' }}>
                {(() => {
                  console.log('analyticsData', analyticsData);
                  if (!analyticsData.campaignDispo || analyticsData.campaignDispo.length === 0) {
                    return <Typography color="text.secondary">No data available</Typography>;
                  }

                  // Filter the data based on campaign filter
                  let filteredCampaigns = Object.keys(campaignDispoMap);
                  if (filters.campaign) {
                    filteredCampaigns = filteredCampaigns.filter(campaign => 
                      campaign.toLowerCase().includes(filters.campaign.toLowerCase())
                    );
                  }

                  // Sort by total count (sum of all dispositions)
                  filteredCampaigns.sort((a, b) => {
                    const totalA = Object.values(campaignDispoMap[a]).reduce((sum, count) => sum + count, 0);
                    const totalB = Object.values(campaignDispoMap[b]).reduce((sum, count) => sum + count, 0);
                    return totalB - totalA;
                  });

                  // Limit to top 10 if not showing all
                  if (!showAllListIds && !filters.campaign) {
                    filteredCampaigns = filteredCampaigns.slice(0, 10);
                  }

                  return (
                    <>
                      {!showAllListIds && !filters.campaign && Object.keys(campaignDispoMap).length > 10 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                          Showing top 10 performing campaigns. Use "Show All" to see complete list.
                        </Alert>
                      )}
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Campaign</TableCell>
                              <TableCell>Dispositions</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {filteredCampaigns.map(campaign => (
                              <TableRow key={campaign}>
                                <TableCell>
                                  <Typography variant="subtitle2" fontWeight="bold">
                                    {campaign}
                                  </Typography>
                                </TableCell>
                                <TableCell>
                                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                                    {allCampaignDispos.map(dispo => {
                                      const count = campaignDispoMap[campaign][dispo] || 0;
                                      if (count === 0) return null;
                                      return (
                                        <Chip
                                          key={dispo}
                                          label={`${dispo}: ${count}`}
                                          size="small"
                                          color={getDispositionColor(dispo)}
                                          variant="outlined"
                                        />
                                      );
                                    })}
                                  </Box>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </>
                  );
                })()}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Search"
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>List ID</InputLabel>
              <Select
                value={filters.list_id}
                label="List ID"
                onChange={(e) => handleFilterChange('list_id', e.target.value)}
              >
                <MenuItem value="">All Lists</MenuItem>
                {listIds.map(listId => (
                  <MenuItem key={listId} value={listId}>
                    {listId}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Campaign</InputLabel>
              <Select
                value={filters.campaign}
                label="Campaign"
                onChange={(e) => handleFilterChange('campaign', e.target.value)}
              >
                <MenuItem value="">All Campaigns</MenuItem>
                {campaigns.map(campaign => (
                  <MenuItem key={campaign} value={campaign}>
                    {campaign}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>VICIdial Dispo</InputLabel>
              <Select
                value={filters.disposition}
                label="VICIdial Dispo"
                onChange={(e) => handleFilterChange('disposition', e.target.value)}
              >
                <MenuItem value="">All Dispositions</MenuItem>
                {(availableDispositions || []).map(disposition => (
                  <MenuItem key={disposition} value={disposition}>
                    {disposition}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth size="small">
              <InputLabel>Lead Status</InputLabel>
              <Select
                value={filters.status}
                label="Lead Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {LEAD_STATUS_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Chip 
                      label={option.label} 
                      color={option.color} 
                      size="small" 
                      sx={{ mr: 1 }}
                    />
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Date From"
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="DD/MM/YYYY format"
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  opacity: 0.7
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '0.7rem',
                  color: 'text.secondary'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Date To"
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="DD/MM/YYYY format"
              sx={{
                '& input[type="date"]::-webkit-calendar-picker-indicator': {
                  opacity: 0.7
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '0.7rem',
                  color: 'text.secondary'
                }
              }}
            />
          </Grid>
          
          {/* Action Buttons Row */}
          <Grid item xs={12}>
            <Box display="flex" gap={1} flexWrap="wrap" justifyContent="space-between" alignItems="center">
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button variant="outlined" size="small" onClick={clearFilters}>
                  Clear Filters
                </Button>
                <IconButton onClick={fetchLeads} color="primary" size="small" title="Refresh">
                  <RefreshIcon />
                </IconButton>
                <IconButton onClick={exportLeads} color="primary" size="small" title="Export CSV">
                  <ExportIcon />
                </IconButton>
                <Button
                  variant="contained"
                  size="small"
                  onClick={openBulkDispoDialog}
                  disabled={selectedLeads.length === 0}
                  startIcon={<DispoIcon />}
                  color="secondary"
                >
                  Bulk Disposition ({selectedLeads.length})
                </Button>
              </Box>
              <Box display="flex" gap={1} flexWrap="wrap">
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setFieldMappingDialogOpen(true)}
                  startIcon={<SettingsIcon />}
                  title="Configure Field Labels"
                >
                  Field Settings
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setDispositionSettingsOpen(true)}
                  startIcon={<AnalyticsIcon />}
                  title="Configure Call Analysis Auto-Trigger"
                >
                  Analysis Settings
                </Button>
              </Box>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Leads Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={selectedLeads.length > 0 && selectedLeads.length < leads.length}
                    checked={leads.length > 0 && selectedLeads.length === leads.length}
                    onChange={handleSelectAll}
                  />
                </TableCell>
                <TableCell>VICIdial Lead ID</TableCell>
                <TableCell>List ID</TableCell>
                <TableCell>Agent & Customer</TableCell>
                <TableCell>Phone Number</TableCell>
                <TableCell>Campaign</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Disposition</TableCell>
                <TableCell>Recording</TableCell>
                <TableCell>Called Count</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : leads.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={12} align="center">
                    No leads found
                  </TableCell>
                </TableRow>
              ) : (
                leads.map((lead) => (
                  <TableRow key={lead.id} hover>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedLeads.includes(lead.id)}
                        onChange={() => handleSelectLead(lead.id)}
                      />
                    </TableCell>
                    <TableCell>{lead.vici_lead_id || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={lead.list_id || 'N/A'} 
                        size="small" 
                        color="default"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Chip 
                          label={`Agent: ${getAgentName(lead)}`} 
                          size="small" 
                          color="primary"
                          variant="outlined"
                          sx={{ mb: 0.5, mr: 0.5 }}
                        />
                        <br />
                        <Typography variant="body2" color="text.secondary">
                          Customer: {lead.fullname || `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'N/A'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>{lead.phone_number || 'N/A'}</TableCell>
                    <TableCell>{lead.campaign || 'N/A'}</TableCell>
                    <TableCell>
                      <Chip 
                        label={lead.status} 
                        color={getStatusColor(lead.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {lead.disposition && (
                        <Chip 
                          label={lead.disposition} 
                          color={getDispositionColor(lead.disposition)}
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {lead.call_id ? (
                        <Box display="flex" alignItems="center" gap={0.5}>
                          <IconButton 
                            size="small"
                            onClick={() => handlePlayRecording(lead)}
                            color={currentPlayingCall === lead.call_id && isPlaying ? "secondary" : "default"}
                            title={currentPlayingCall === lead.call_id && isPlaying ? "Pause" : "Play Recording"}
                          >
                            {currentPlayingCall === lead.call_id && isPlaying ? (
                              <PauseIcon fontSize="small" />
                            ) : (
                              <PlayArrowIcon fontSize="small" />
                            )}
                          </IconButton>
                          {currentPlayingCall === lead.call_id && (
                            <IconButton 
                              size="small"
                              onClick={handleAudioStop}
                              color="default"
                              title="Stop Recording"
                            >
                              <StopIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          No recording
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={lead.called_count || 0} 
                        size="small" 
                        color={lead.called_count > 3 ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell>{formatDate(lead.created_at, true)}</TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleLeadClick(lead)}
                          color="primary"
                        >
                          <VisibilityIcon />
                        </IconButton>
                        <IconButton 
                          size="small" 
                          onClick={() => openDispoDialog(lead)}
                          color="secondary"
                        >
                          <EditIcon />
                        </IconButton>
                        {autoTriggerDispositions.includes(lead.disposition) && (
                          <IconButton 
                            size="small" 
                            onClick={() => handleMoveToAnalysis(lead)}
                            color="success"
                            title="Send to Call Analysis"
                          >
                            <AnalyticsIcon />
                          </IconButton>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      {/* Hidden Audio Element */}
      {audioUrl && (
        <audio
          ref={(audio) => {
            if (audio) {
              audio.onplay = handleAudioPlay;
              audio.onpause = handleAudioPause;
              audio.onended = handleAudioStop;
              audio.onerror = handleAudioError;
              if (isPlaying && audio.paused) {
                audio.play().catch(handleAudioError);
              } else if (!isPlaying && !audio.paused) {
                audio.pause();
              }
            }
          }}
          src={audioUrl}
          style={{ display: 'none' }}
        />
      )}

      {/* Disposition Dialog */}
      <Dialog open={dispoDialogOpen} onClose={() => setDispoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Set Lead Disposition</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Disposition</InputLabel>
              <Select
                value={newDisposition}
                label="Disposition"
                onChange={(e) => setNewDisposition(e.target.value)}
              >
                {LEAD_STATUS_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Chip 
                      label={option.label} 
                      color={option.color} 
                      size="small" 
                      sx={{ mr: 1 }}
                    />
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              margin="normal"
              label="Notes"
              multiline
              rows={3}
              value={dispoNotes}
              onChange={(e) => setDispoNotes(e.target.value)}
              placeholder="Add any additional notes..."
            />
            {autoTriggerDispositions.includes(newDisposition) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This disposition will automatically move the lead to Call Analysis & Transcription queue.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDispoDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDispositionUpdate} 
            variant="contained"
            disabled={!newDisposition}
          >
            Update Disposition
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Disposition Dialog */}
      <Dialog open={bulkDispoDialogOpen} onClose={() => setBulkDispoDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Bulk Update Lead Disposition</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            You are about to update disposition for {selectedLeads.length} selected leads.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel>Disposition</InputLabel>
              <Select
                value={bulkDisposition}
                label="Disposition"
                onChange={(e) => setBulkDisposition(e.target.value)}
              >
                {LEAD_STATUS_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    <Chip 
                      label={option.label} 
                      color={option.color} 
                      size="small" 
                      sx={{ mr: 1 }}
                    />
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              margin="normal"
              label="Notes"
              multiline
              rows={3}
              value={bulkNotes}
              onChange={(e) => setBulkNotes(e.target.value)}
              placeholder="Add any additional notes for all selected leads..."
            />
            {autoTriggerDispositions.includes(bulkDisposition) && (
              <Alert severity="info" sx={{ mt: 2 }}>
                This disposition will automatically move the selected leads to Call Analysis & Transcription queue.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDispoDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleBulkDispositionUpdate} 
            variant="contained"
            disabled={!bulkDisposition}
            color="secondary"
          >
            Update {selectedLeads.length} Leads
          </Button>
        </DialogActions>
      </Dialog>

      {/* Lead Detail Dialog */}
      <Dialog open={detailDialogOpen} onClose={() => setDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Lead Details - {selectedLead?.fullname || selectedLead?.first_name + ' ' + selectedLead?.last_name}
            </Typography>
            <Box>
              {/* Edit button for admin/qa users */}
              {user && ['admin', 'qa-user', 'superadmin'].includes(user.role) && (
                <IconButton 
                  onClick={editMode ? handleCancelEdit : handleEditMode}
                  title={editMode ? "Cancel Edit" : "Edit Lead"}
                  color={editMode ? "default" : "primary"}
                >
                  {editMode ? <CloseIcon /> : <EditIcon />}
                </IconButton>
              )}
              <IconButton 
                onClick={() => setFieldMappingDialogOpen(true)}
                title="Configure Field Labels"
              >
                <SettingsIcon />
              </IconButton>
              <IconButton onClick={() => setDetailDialogOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedLead && (
            <Grid container spacing={3}>
              {/* Lead Supplier Information */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Lead Supplier Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography><strong>{getFieldLabel('vici_lead_id', 'VICIdial Lead ID')}:</strong> {selectedLead.vici_lead_id || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography><strong>{getFieldLabel('list_id', 'List ID')}:</strong> {selectedLead.list_id || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography><strong>{getFieldLabel('vendor_lead_code', 'Vendor Lead Code')}:</strong> {selectedLead.vendor_lead_code || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography><strong>{getFieldLabel('campaign', 'Campaign')}:</strong> {selectedLead.campaign || 'N/A'}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Customer Information */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Customer Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          label={getFieldLabel('fullname', 'Full Name')}
                          value={editedLead.fullname || ''}
                          onChange={(e) => handleFieldChange('fullname', e.target.value)}
                        />
                      ) : (
                        <Typography><strong>{getFieldLabel('fullname', 'Full Name')}:</strong> {selectedLead.fullname || `${selectedLead.first_name || ''} ${selectedLead.last_name || ''}`.trim() || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          label={getFieldLabel('title', 'Title')}
                          value={editedLead.title || ''}
                          onChange={(e) => handleFieldChange('title', e.target.value)}
                        />
                      ) : (
                        <Typography><strong>{getFieldLabel('title', 'Title')}:</strong> {selectedLead.title || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          label={getFieldLabel('date_of_birth', 'Date of Birth')}
                          type="date"
                          value={editedLead.date_of_birth ? editedLead.date_of_birth.split('T')[0] : ''}
                          onChange={(e) => handleFieldChange('date_of_birth', e.target.value)}
                          InputLabelProps={{ shrink: true }}
                        />
                      ) : (
                        <Typography><strong>{getFieldLabel('date_of_birth', 'Date of Birth')}:</strong> {selectedLead.date_of_birth ? new Date(selectedLead.date_of_birth).toLocaleDateString() : 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          label={getFieldLabel('security_phrase', 'Security Phrase')}
                          value={editedLead.security_phrase || ''}
                          onChange={(e) => handleFieldChange('security_phrase', e.target.value)}
                        />
                      ) : (
                        <Typography><strong>{getFieldLabel('security_phrase', 'Security Phrase')}:</strong> {selectedLead.security_phrase || 'N/A'}</Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Contact Information */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Contact Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          label={getFieldLabel('phone_number', 'Phone Number')}
                          value={editedLead.phone_number || ''}
                          onChange={(e) => handleFieldChange('phone_number', e.target.value)}
                        />
                      ) : (
                        <Typography><strong>{getFieldLabel('phone_number', 'Phone Number')}:</strong> {selectedLead.phone_number || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          label={getFieldLabel('alt_phone', 'Alternative Phone')}
                          value={editedLead.alt_phone || ''}
                          onChange={(e) => handleFieldChange('alt_phone', e.target.value)}
                        />
                      ) : (
                        <Typography><strong>{getFieldLabel('alt_phone', 'Alternative Phone')}:</strong> {selectedLead.alt_phone || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          label={getFieldLabel('dialed_number', 'Dialed Number')}
                          value={editedLead.dialed_number || ''}
                          onChange={(e) => handleFieldChange('dialed_number', e.target.value)}
                        />
                      ) : (
                        <Typography><strong>{getFieldLabel('dialed_number', 'Dialed Number')}:</strong> {selectedLead.dialed_number || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          label={getFieldLabel('email', 'Email Address')}
                          type="email"
                          value={editedLead.email || ''}
                          onChange={(e) => handleFieldChange('email', e.target.value)}
                        />
                      ) : (
                        <Typography><strong>{getFieldLabel('email', 'Email Address')}:</strong> {selectedLead.email || 'N/A'}</Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Address Information */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Address Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          label={getFieldLabel('address1', 'Address Line 1')}
                          value={editedLead.address1 || ''}
                          onChange={(e) => handleFieldChange('address1', e.target.value)}
                        />
                      ) : (
                        <Typography><strong>{getFieldLabel('address1', 'Address Line 1')}:</strong> {selectedLead.address1 || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          label={getFieldLabel('address2', 'Address Line 2')}
                          value={editedLead.address2 || ''}
                          onChange={(e) => handleFieldChange('address2', e.target.value)}
                        />
                      ) : (
                        <Typography><strong>{getFieldLabel('address2', 'Address Line 2')}:</strong> {selectedLead.address2 || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          label={getFieldLabel('address3', 'Address Line 3')}
                          value={editedLead.address3 || ''}
                          onChange={(e) => handleFieldChange('address3', e.target.value)}
                        />
                      ) : (
                        <Typography><strong>{getFieldLabel('address3', 'Address Line 3')}:</strong> {selectedLead.address3 || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          label={getFieldLabel('city', 'City')}
                          value={editedLead.city || ''}
                          onChange={(e) => handleFieldChange('city', e.target.value)}
                        />
                      ) : (
                        <Typography><strong>{getFieldLabel('city', 'City')}:</strong> {selectedLead.city || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          label={getFieldLabel('postal_code', 'Postal Code')}
                          value={editedLead.postal_code || ''}
                          onChange={(e) => handleFieldChange('postal_code', e.target.value)}
                        />
                      ) : (
                        <Typography><strong>{getFieldLabel('postal_code', 'Postal Code')}:</strong> {selectedLead.postal_code || 'N/A'}</Typography>
                      )}
                    </Grid>
                    <Grid item xs={4}>
                      {editMode ? (
                        <TextField
                          fullWidth
                          label={getFieldLabel('county', 'County/Province')}
                          value={editedLead.county || ''}
                          onChange={(e) => handleFieldChange('county', e.target.value)}
                        />
                      ) : (
                        <Typography><strong>{getFieldLabel('county', 'County/Province')}:</strong> {selectedLead.county || 'N/A'}</Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Additional custom mapped fields */}
              {Object.keys(fieldMappings).map(field => {
                const mapping = fieldMappings[field];
                if (!mapping.visible || ['vici_lead_id', 'list_id', 'vendor_lead_code', 'campaign', 'fullname', 'title', 'date_of_birth', 'security_phrase', 'phone_number', 'alt_phone', 'dialed_number', 'email', 'address1', 'address2', 'address3', 'city', 'postal_code', 'county'].includes(field)) {
                  return null;
                }

                return (
                  <Grid item xs={12} key={field}>
                    <Paper sx={{ p: 2 }}>
                      <Typography variant="h6" gutterBottom color="primary">
                        {mapping.label}
                      </Typography>
                      <Typography>{selectedLead[field] || 'N/A'}</Typography>
                      {mapping.description && (
                        <Typography variant="caption" color="textSecondary">
                          {mapping.description}
                        </Typography>
                      )}
                    </Paper>
                  </Grid>
                );
              })}

              {/* Call Information */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Call Information
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography><strong>Called Count:</strong> {selectedLead.called_count || 0}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      {editMode ? (
                        <FormControl fullWidth>
                          <InputLabel>Status</InputLabel>
                          <Select
                            value={editedLead.status || ''}
                            onChange={(e) => handleFieldChange('status', e.target.value)}
                            label="Status"
                          >
                            {LEAD_STATUS_OPTIONS.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography><strong>Status:</strong> 
                          <Chip 
                            label={selectedLead.status} 
                            color={getStatusColor(selectedLead.status)}
                            size="small"
                            sx={{ ml: 1 }}
                          />
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      {editMode ? (
                        <FormControl fullWidth>
                          <InputLabel>Disposition</InputLabel>
                          <Select
                            value={editedLead.disposition || ''}
                            onChange={(e) => handleFieldChange('disposition', e.target.value)}
                            label="Disposition"
                          >
                            {DISPOSITION_OPTIONS.map(option => (
                              <MenuItem key={option.value} value={option.value}>
                                {option.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography><strong>Disposition:</strong>
                          {selectedLead.disposition && (
                            <Chip 
                              label={selectedLead.disposition} 
                              color={getDispositionColor(selectedLead.disposition)}
                              size="small"
                              sx={{ ml: 1 }}
                            />
                          )}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6}>
                      <Typography><strong>Created:</strong> {formatDate(selectedLead.created_at, true)}</Typography>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>

              {/* Comments */}
              <Grid item xs={12}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom color="primary">
                    Comments
                  </Typography>
                  {editMode ? (
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label="Comments"
                      value={editedLead.comments || ''}
                      onChange={(e) => handleFieldChange('comments', e.target.value)}
                    />
                  ) : (
                    <Typography>{selectedLead.comments || 'No comments'}</Typography>
                  )}
                </Paper>
              </Grid>

              {/* Call Analysis & Transcription */}
              {selectedLead.Call && (
                <Grid item xs={12}>
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Call Analysis & Transcription
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography><strong>Call Date:</strong> {selectedLead.Call.call_date ? new Date(selectedLead.Call.call_date).toLocaleDateString() : 'N/A'}</Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography><strong>Recording:</strong> {selectedLead.Call.recording_url ? 'Available' : 'Not Available'}</Typography>
                      </Grid>
                    </Grid>
                    {selectedLead.Call.recording_url && (
                      <Box mt={2}>
                        <Button
                          variant="outlined"
                          color="primary"
                          onClick={() => window.open(selectedLead.Call.recording_url, '_blank')}
                          startIcon={<PlayArrowIcon />}
                        >
                          Play Recording
                        </Button>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {editMode && (
            <Button
              variant="contained"
              color="primary"
              onClick={handleSaveEdit}
              startIcon={<CheckCircleIcon />}
            >
              Save Changes
            </Button>
          )}
          {autoTriggerDispositions.includes(selectedLead?.disposition) && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleMoveToAnalysis(selectedLead)}
              startIcon={<AssessmentIcon />}
            >
              Move to Call Analysis & Transcription
            </Button>
          )}
          <Button onClick={() => setDetailDialogOpen(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Field Mapping Manager Dialog */}
      <FieldMappingManager
        open={fieldMappingDialogOpen}
        onClose={() => {
          setFieldMappingDialogOpen(false);
          fetchLeads(); // Refresh leads to get updated field mappings
        }}
        campaign={filters.campaign || null}
      />

      {/* Disposition Settings Dialog */}
      <DispositionSettings
        open={dispositionSettingsOpen}
        onClose={() => setDispositionSettingsOpen(false)}
        onSave={handleSaveDispositionSettings}
        currentSettings={autoTriggerDispositions}
        availableDispositions={availableDispositions}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Leads; 